/**
 * Product Comparison Tool
 *
 * This script is used to compare products.
 * It uses the Spec and Compare tool logic and builds a new more custom compare tool on top.
 *
 * The code has the following features:
 * - It uses the Spec and Compare tool logic to compare products
 * - It uses the comparison-all-tables.liquid template to fetch all spec tables data
 * - It uses the comparison-bar.liquid template to render the comparison bar
 * - It uses the product-compare-checkbox.liquid template to render the checkboxes
 *
 * Fetch and render products from the comparision localstorage and render them into the table
 *
 * It also requires the following Shopify Liquid templates:
 *  product.collections-json.liquid
 *  product.comparision-json.liquid
 *  comparison-all-tables.liquid
 *  comparison-bar.liquid
 *  product-compare-checkbox.liquid
 *  product-compare-checkbox.liquid
 */

// Prevent duplicate initialization
if (!window.ComparisonTool) {
  class ComparisonTool {
    constructor() {
      this.dynamicCompareInstance = null;
      this.isLoggedIn = false;
      this.translations = this.loadTranslations();
      this.faviconImageSrc = window.shopSettings.faviconUrl;

      this.init();
    }

    init() {
      // Check if customer is logged in
      this.isLoggedIn = document.querySelector('[data-comparison-customer]');

      // Wait for sc-dynamic-compare to be available
      this.waitForDynamicCompare();

      // Initialize UI elements
      this.comparisonBar = document.querySelector('[data-comparison-bar]');
      this.comparisonCount = document.querySelector('[data-comparison-count]');
      this.clearButton = document.querySelector('[data-clear-comparison]');
      this.openButton = document.querySelector('[data-open-comparison]');
      this.comparisonModal = document.querySelector('[data-comparison-modal]');
      this.comparisonTable = document.querySelector('[data-comparison-table]');
      this.compareCheckboxes = document.querySelectorAll('.compare-app');

      // Bind events
      this.bindEvents();

      // Start polling for count updates
      this.startCountPolling();
      this.preFetchComparisonData();
    }

    waitForDynamicCompare() {
      const checkForInstance = () => {
        this.dynamicCompareInstance = document.querySelector('sc-dynamic-compare');
        if (!this.dynamicCompareInstance) {
          setTimeout(checkForInstance, 100);
        }
      };
      checkForInstance();
    }

    startCountPolling() {
      setInterval(() => {
        this.updateUI();
      }, 500);
    }

    loadTranslations() {
      // These will be set by Shopify Liquid translations
      return {
        clearAll: window.comparisonTranslations?.clearAll || 'Clear All',
        compare: window.comparisonTranslations?.compare || 'Compare',
        compareProducts: window.comparisonTranslations?.compareProducts || 'Compare Products',
        specifications: window.comparisonTranslations?.specifications || 'Specifications',
        price: window.comparisonTranslations?.price || 'Price',
        loginToSeePrice: window.comparisonTranslations?.loginToSeePrice || 'Login to see price',
        requestPrice: window.comparisonTranslations?.requestPrice || 'Request price',
        quantity: window.comparisonTranslations?.quantity || 'Quantity',
        addToCart: window.comparisonTranslations?.addToCart || 'Add to Cart',
        addToCompare: window.comparisonTranslations?.addToCompare || 'Add to Compare',
        removeFromCompare: window.comparisonTranslations?.removeFromCompare || 'Remove from Compare',
        groups: window.comparisonTranslations?.groups || {},
        attributes: window.comparisonTranslations?.attributes || {}
      };
    }

    toSnakeCase(str) {
      return str.toLowerCase().replace(/\s+/g, '_');
    }

    translateAttribute(key) {
      const snakeKey = this.toSnakeCase(key);
      return this.translations.attributes[snakeKey] || key;
    }

    translateGroup(key) {
      const snakeKey = this.toSnakeCase(key);
      // Map known group names to their translation keys
      const keyMapping = {
        'attributes': 'panel_attributes'
      };

      const mappedKey = keyMapping[snakeKey] || snakeKey;
      return this.translations.groups[mappedKey] || key;
    }

    bindEvents() {
      // Clear button
      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => this.clearProducts());
      }

      this.compareCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener('click', (e) => {
          // Wait for sc-dynamic-compare to process the change
          setTimeout(() => {
            this.preFetchComparisonData();
            this.updateUI();
          }, 100);
        });
      });

      // Open modal button - show pre-loaded modal
      if (this.openButton) {
        this.openButton.addEventListener('click', () => this.openModal());
      }

      // Close modal
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-modal-close]') || e.target.closest('[data-modal-close]')) {
          this.closeModal();
        }
      });

      if (this.comparisonModal) {
        this.comparisonModal.addEventListener('click', (e) => {
          // Check if click is directly on the modal backdrop (not on modal content)
          if (e.target === this.comparisonModal) {
            this.closeModal();
          }
        });
      }

      // ESC key to close modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.comparisonModal && this.comparisonModal.classList.contains('active')) {
          this.closeModal();
        }
      });
    }

    async preFetchComparisonData() {
      if (!this.dynamicCompareInstance) return;

      const count = this.dynamicCompareInstance.getCount();
      if (count < 2) return;

      const handles = this.dynamicCompareInstance.getHandles();

      // Fetch and render products in background
      await this.fetchAndRenderProducts(handles);
    }

    async openModal() {
      if (!this.dynamicCompareInstance) return;

      const count = this.dynamicCompareInstance.getCount();
      if (count < 2) return;

      // Show modal immediately (table should already be rendered from pre-fetch)
      if (this.comparisonModal) {
        this.comparisonModal.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    }

    clearProducts() {
      if (this.dynamicCompareInstance) {
        this.dynamicCompareInstance.clearProducts();
      }
      this.updateUI();
    }

    closeModal() {
      if (this.comparisonModal) {
        this.comparisonModal.classList.remove('active');
        document.body.style.overflow = '';
      }
    }

    async fetchAndRenderProducts(handles) {
      if (!handles || handles.length < 2) return;

      try {
        // Load all spec tables data (for matching by collection)
        const allTablesData = this.loadAllTablesData();

        const productsWithSpecs = [];

        for (const handle of handles) {
          // 1. First check embedded spec data on current page
          const specDataElement = document.querySelector(`[data-product-specs="${handle}"]`);
          if (specDataElement) {
            const productData = JSON.parse(specDataElement.textContent);
            productsWithSpecs.push(productData);
            continue;
          }

          // 2. Fetch product data with metafields from comparison template
          const response = await fetch(`${window.Shopify.routes.root}products/${handle}?view=comparison-json`);

          const product = await response.json();

          // Try to find matching spec table based on product's collections
          const specs = await this.getSpecsForProduct(product, allTablesData);

          productsWithSpecs.push({
            product_handle: product.product_handle || product.handle,
            product_id: product.product_id || product.id,
            product_title: product.product_title || product.title,
            product_image: product.product_image || product.featured_image,
            product_price: product.product_price || product.price,
            product_url: product.product_url || product.url,
            variant_id: product.variant_id || product.variants?.[0]?.id,
            vendor: product.vendor,
            type: product.type,
            sku: product.sku,
            weight: product.weight,
            metafields: product.metafields,
            specifications: specs
          });
        }

        this.renderComparisonTable(productsWithSpecs);
      } catch (error) {
        console.error('❌ Error fetching product data:', error);
        this.showNotification('Error loading comparison data');
      }
    }

    loadAllTablesData() {
      const tablesElement = document.getElementById('comparison-all-tables-data');
      if (!tablesElement) {
        return { tables: [] };
      }
      try {
        return JSON.parse(tablesElement.textContent);
      } catch (e) {
        console.error('Error parsing tables data:', e);
        return { tables: [] };
      }
    }

    async getSpecsForProduct(product, allTablesData) {
      if (!allTablesData.tables || allTablesData.tables.length === 0) {
        return {};
      }

      // Get product's collections - first try from product data (comparison-json template)
      let productCollectionIds = product.collection_ids || [];

      // Fallback: fetch if not included in product data
      if (productCollectionIds.length === 0) {
        try {
          const collectionsResponse = await fetch(`${window.Shopify.routes.root}products/${product.product_handle || product.handle}?view=collections-json`);
          if (collectionsResponse.ok) {
            const collectionsData = await collectionsResponse.json();
            productCollectionIds = collectionsData.collection_ids || [];
          }
        } catch (e) {
          console.error('⚠️ Could not fetch product collections');
        }
      }

      // Fallback: check product.collections property
      if (productCollectionIds.length === 0 && product.collections) {
        productCollectionIds = product.collections.map(c => c.id);
      }

      const handle = product.product_handle || product.handle;

      // Find matching table
      let matchingTable = null;
      for (const table of allTablesData.tables) {
        // Parse rule_set_string to extract collection IDs
        let tableCollectionIds = [];
        if (table.rule_set_string) {
          try {
            const ruleSet = JSON.parse(table.rule_set_string);
            if (ruleSet.rules && ruleSet.rules.length > 0) {
              tableCollectionIds = ruleSet.rules
              .filter(rule => rule.column === 'collection')
              .map(rule => String(rule.condition));
            }
          } catch (e) {
            console.error(`⚠️ Could not parse rule_set for ${table.name}`);
          }
        }

        // Check if any product collection matches table collection
        for (const tableCollId of tableCollectionIds) {
          for (const productCollId of productCollectionIds) {
            if (tableCollId === String(productCollId)) {
              matchingTable = table;
              break;
            }
          }
          if (matchingTable) break;
        }

        if (matchingTable) break;
      }

      if (!matchingTable) {
        return {};
      }

      // Extract specs from matching table (using metafields from product)
      return this.extractSpecsFromTable(product, matchingTable);
    }

    extractSpecsFromTable(product, table) {
      const specs = {};

      for (const group of table.groups) {
        for (const attr of group.attributes) {
          const specValue = this.getSpecValue(product, attr);
          if (specValue !== null) {
            specs[attr.name] = {
              group: group.name,
              type: attr.type,
              value: specValue
            };
          }
        }
      }

      return specs;
    }

    getSpecValue(product, attribute) {
      switch (attribute.type) {
        case 'product_price':
          return product.product_price || product.price;
        case 'product_type':
          return product.type;
        case 'product_vendor':
          return product.vendor;
        case 'product_sku':
          return product.sku || product.variants?.[0]?.sku;
        case 'product_weight':
          return product.weight || product.variants?.[0]?.weight;
        default:
          // For metafield values, check the metafields object
          if (attribute.metafield_reference && product.metafields) {
            const { namespace, key } = attribute.metafield_reference;
            const metafieldValue = product.metafields[namespace]?.[key];

            if (metafieldValue !== undefined && metafieldValue !== null && metafieldValue !== '') {
              // If it's a dimension object with value and unit, format it properly
              if (typeof metafieldValue === 'object' && metafieldValue.value !== undefined && metafieldValue.unit !== undefined) {
                return `${metafieldValue.value} ${metafieldValue.unit}`;
              }
              return metafieldValue;
            }
            return null;
          }
          return null;
      }
    }

    async renderComparisonTable(products) {
      if (!this.comparisonTable || products.length < 2) return;

      // Fetch current cart quantities
      const variantIds = products.map(p => p.variant_id);
      const cartQuantities = await this.getCurrentCartQuantities(variantIds);

      // Collect all unique spec keys that are comparable - organized by group
      const specsByGroup = {};

      products.forEach(product => {
        if (product.specifications) {
          Object.entries(product.specifications).forEach(([key, specData]) => {
            const groupName = specData.group;
            if (!specsByGroup[groupName]) {
              specsByGroup[groupName] = new Set();
            }
            specsByGroup[groupName].add(key);
          });
        }
      });

      // Build table HTML
      let html = `
      <thead>
        <tr>
          <th>${ window.comparisonTranslations?.specifications }</th>
          ${products.map(product => `
            <th>
              <div class="comparison-table__product-image">
                <button class="comparison-table__remove-product"
                        onclick="comparisonTool.removeProduct('${product.product_handle}')"
                        aria-label="Remove ${product.product_title} from comparison">
                  ×
                </button>
                <a href="${product.product_url}" class="${product.product_image ? '' : 'comparison-table__product-link--no-image'}" aria-label="Go to ${product.product_title}">
                  <img src="${product.product_image || this.faviconImageSrc}" alt="${product.product_title}">
                </a>
              </div>
              <a class="comparison-table__product-link" href="${product.product_url}" aria-label="Go to ${product.product_title}">
                <p class="comparison-table__product-name">${product.product_title}</p>
              </a>
            </th>
          `).join('')}
        </tr>
      </thead>
      <tbody>
        <!-- Price Row -->
        <tr>
          <td><strong>${ window.comparisonTranslations?.price }</strong></td>
          ${products.map(product => `
            <td>
              ${this.isLoggedIn
        ? product.metafields?.ecostal?.hide_price == true
          ? `<a class="button button--black" href="${product.product_url}" rel="nofollow">
                  <span>${window.comparisonTranslations?.requestPrice || this.translations.requestPrice}</span>
                </a>`
          : `<span class="comparison-table__price">${this.formatMoney(product.product_price)}</span>`
        : `<span class="comparison-table__login-message">${window.comparisonTranslations?.loginToSeePrice}</span>`
      }
            </td>
          `).join('')}
        </tr>
  
        ${this.isLoggedIn
        ? `
          <!-- Add to Cart Row -->
          <tr>
            <td><strong>${ window.comparisonTranslations?.addToCart }</strong></td>
            ${products.map(product => `
              <td>
                  <div class="collection-quick-add-bulk" data-id="comparison-tool">
                    <quick-add-bulk
                      data-min="1"
                      data-max=""
                      data-step="1"
                      id="quick-add-bulk-${product.product_id}-comparison-tool"
                      class="quick-add-bulk"
                      data-index="${product.variant_id}"
                      data-id="${product.product_id}"
                    >
                      <div class="comparison-table__add-cart">
                        <quantity-input class="quantity" data-min="1">
                          <button class="quantity__button" name="minus" type="button">
                            <span class="visually-hidden">Decrease quantity for ${product.product_title}</span>
                            <span class="svg-wrapper">-</span>
                          </button>
                          <input
                            class="quantity__input"
                            type="number"
                            name="quantity"
                            id="Quantity-${product.variant_id}"
                            data-quantity="${product.product_handle}"
                            data-variant="${product.variant_id}"
                            data-index="${product.variant_id}"
                            min="1"
                            step="1"
                            value="${cartQuantities[product.variant_id] || 0}"
                          >
                          <button class="quantity__button" name="plus" type="button">
                            <span class="visually-hidden">Increase quantity for ${product.product_title}</span>
                            <span class="svg-wrapper">+</span>
                          </button>
                          <div class="progress-bar-container hidden">
                            <div class="progress-bar">
                              <div class="progress-bar-value"></div>
                            </div>
                          </div>
                        </quantity-input>
                      </div>
                    </quick-add-bulk>
                  </div>
              </td>
            `).join('')}
          </tr>`
        : ``
        }
  
        <!-- Specification Groups and Rows -->
        ${Object.entries(specsByGroup).map(([groupName, specs]) => {
        const specsArray = Array.from(specs);
        const translatedGroupName = this.translateGroup(groupName);
        
        return `
            <!-- Group Header -->
            <tr class="comparison-table__group-header">
              <td colspan="${products.length + 1}"><strong>${translatedGroupName}</strong></td>
            </tr>
            <!-- Group Specs -->
            ${specsArray.map(spec => {
          const translatedSpec = this.translateAttribute(spec);
          return `
              <tr>
                <td><strong>${translatedSpec}</strong></td>
                ${products.map(product => {
                  const specData = product.specifications?.[spec];
                  let value = specData?.value ?? '-';
                  
                  // Handle object values (dimension metafields)
                  if (value && typeof value === 'object') {
                    if (value.value !== undefined && value.unit !== undefined) {
                      value = `${value.value} ${value.unit}`;
                    } else {
                      value = JSON.stringify(value);
                    }
                  }
                  
                  // Fix malformed dimension strings (e.g., "1899.0 899 mm" -> "1899.0 mm")
                  if (typeof value === 'string' && value.match(/^\d+\.\d+\s+\d{3}\s+\w+$/)) {
                    value = value.replace(/^(\d+\.\d+)\s+\d{3}\s+(\w+)$/, '$1 $2');
                  }
                  
                  return `<td>${value}</td>`;
                }).join('')}
              </tr>
            `}).join('')}
          `;
      }).join('')}
      </tbody>
    `;

      this.comparisonTable.innerHTML = html;

      await customElements.whenDefined('quick-add-bulk');

      // Patch quick-add-bulk instances
      const quickAddBulkElements = this.comparisonTable.querySelectorAll('quick-add-bulk');

      quickAddBulkElements.forEach(element => {
        // Override getSectionsToRender to only return cart sections
        element.getSectionsToRender = function() {
          return [
            {
              id: 'cart-icon-bubble',
              section: 'cart-icon-bubble',
              selector: '.shopify-section',
            },
            {
              id: 'cart-icon-bubble-mobile',
              section: 'cart-icon-bubble',
              selector: '.shopify-section',
            },
            {
              id: 'CartDrawer',
              selector: '#CartDrawer',
              section: 'cart-drawer',
            },
          ];
        };

        // Store original updateMultipleQty to wrap it
        const originalUpdateMultipleQty = element.updateMultipleQty.bind(element);

        element.updateMultipleQty = async function(items) {
          // Show progress bar
          const progressBar = this.querySelector('.progress-bar-container');
          if (progressBar) {
            progressBar.classList.remove('hidden');
          }

          // Call original method and wait for it to complete
          try {
            await originalUpdateMultipleQty(items);

            // Wait a brief moment for the component to update its own input
            await new Promise(resolve => setTimeout(resolve, 50));

            // After update completes, call onCartUpdate if it exists
            if (this.onCartUpdate) {
              await this.onCartUpdate();
            }
          } catch (error) {
            console.error('Error updating cart:', error);
            // Hide progress bar on error too
            if (progressBar) {
              progressBar.classList.add('hidden');
            }
          }
        };

        element.onCartUpdate = async function() {
          const response = await fetch(`${window.Shopify.routes.root}cart.js`);
          const cart = await response.json();
          const variantId = parseInt(this.dataset.index);
          const item = cart.items.find(i => i.variant_id == variantId);

          // Update all inputs EXCEPT the one in the current quick-add-bulk instance
          const inputs = document.querySelectorAll(`.quantity__input[data-index="${variantId}"]`);

          inputs.forEach(input => {
            // Skip if this input is inside the current quick-add-bulk element
            if (!this.contains(input)) {
              input.value = item ? item.quantity + 1 : 0;
            }
          });

          // Hide progress bar within THIS quick-add-bulk element
          const progressBar = this.querySelector('.progress-bar-container');
          if (progressBar) {
            progressBar.classList.add('hidden');
          }
        };
      });
    }

    addToCart(variantId, handle) {
      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
      .then(response => response.json())
      .then(data => {
        this.updateCartQuantityDisplay(handle);
        this.showNotification(`Added to cart: ${data.product_title}`);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(error => {
        this.showNotification('Error adding to cart');
      });
    }

    async getCurrentCartQuantities(variantIds) {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cart = await response.json();

        const quantities = {};
        variantIds.forEach(variantId => {
          const item = cart.items.find(i => i.variant_id == variantId);
          quantities[variantId] = item ? item.quantity : 0;
        });

        return quantities;
      } catch (error) {
        return {};
      }
    }

    removeProduct(handle) {
      if (!this.dynamicCompareInstance) return;

      // Show loader
      this.showLoader();

      // Remove the product from the comparison list
      this.dynamicCompareInstance.removeProduct(handle);

      // Get updated handles
      const handles = this.dynamicCompareInstance.getHandles();

      // If less than 2 products remain, close the modal
      if (handles.length < 2) {
        this.hideLoader();
        this.closeModal();
        this.updateUI();
        return;
      }

      // Re-fetch and render the updated comparison table
      this.fetchAndRenderProducts(handles).finally(() => {
        this.hideLoader();
      });
    }

    showLoader() {
      if (!this.comparisonTable) return;

      const container = this.comparisonTable.parentElement;
      if (!container) return;

      // Remove existing loader first
      const existingLoader = container.querySelector('.comparison-loader');
      if (existingLoader) {
        existingLoader.remove();
      }

      // Create new loader
      const loader = document.createElement('div');
      loader.className = 'comparison-loader active';
      loader.innerHTML = '<div class="comparison-loader__spinner"></div>';

      // Ensure container has relative positioning
      if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }

      container.appendChild(loader);
    }

    hideLoader() {
      if (!this.comparisonTable) return;

      const container = this.comparisonTable.parentElement;
      const loader = container?.querySelector('.comparison-loader');

      if (loader) {
        loader.remove();
      }
    }

    removeFromCart(variantId, handle) {
      // First, get the current quantity from the input
      const input = document.querySelector(`[data-quantity="${handle}"]`);
      const currentQuantity = parseInt(input?.value || 0);

      // If quantity is already 0, do nothing
      if (currentQuantity === 0) return;

      // Decrease by 1
      const newQuantity = currentQuantity - 1;

      fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: newQuantity
        })
      })
      .then(response => response.json())
      .then(() => {
        this.updateCartQuantityDisplay(handle);
        if (newQuantity === 0) {
          this.showNotification('Removed from cart');
        } else {
          this.showNotification('Quantity updated');
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(error => {
        this.showNotification('Error removing from cart');
      });
    }

    updateCartQuantityDisplay(handle) {
      const input = document.querySelector(`[data-quantity="${handle}"]`);
      if (!input) return;

      const variantId = input.getAttribute('data-variant');

      fetch(`${window.Shopify.routes.root}cart.js`)
      .then(response => response.json())
      .then(cart => {
        const item = cart.items.find(i => i.variant_id == variantId);
        input.value = item ? item.quantity : 0;
      });
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      });
    }

    updateUI() {
      if (!this.dynamicCompareInstance) return;

      const count = this.dynamicCompareInstance.getCount();

      // Update count
      if (this.comparisonCount) {
        this.comparisonCount.textContent = count;
      }

      // Update bar visibility
      if (this.comparisonBar) {
        if (count > 0) {
          this.comparisonBar.classList.remove('hidden');
        } else {
          this.comparisonBar.classList.add('hidden');
        }
      }

      // Update open button state
      if (this.openButton) {
        this.openButton.disabled = count < 2;
      }
    }

    showNotification(message) {
      const notification = document.createElement('div');
      notification.className = 'comparison-notification';
      notification.textContent = message;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);
    }
  }

// Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.comparisonTool) {
        window.comparisonTool = new ComparisonTool();
      }
    });
  } else {
    if (!window.comparisonTool) {
      window.comparisonTool = new ComparisonTool();
    }
  }
}
